import { useCallback, useEffect, useState } from 'react';
import {
  deleteGuestbook,
  deleteRsvp,
  listGuestbook,
  listRsvp,
} from '@/lib/weddingApi';
import type { GuestbookEntry, RsvpEntry } from '@/types/wedding';

interface Props {
  invitationId: string;
}

type TabKey = 'rsvp' | 'guestbook';

function formatDateTime(value: string): string {
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(
      d.getDate(),
    ).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(
      2,
      '0',
    )}`;
  } catch {
    return '';
  }
}

export default function ResponsesPanel({ invitationId }: Props) {
  const [tab, setTab] = useState<TabKey>('rsvp');
  const [rsvp, setRsvp] = useState<RsvpEntry[]>([]);
  const [guestbook, setGuestbook] = useState<GuestbookEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!invitationId) return;
    setLoading(true);
    setError('');
    try {
      const [r, g] = await Promise.all([
        listRsvp(invitationId),
        listGuestbook(invitationId),
      ]);
      setRsvp(r);
      setGuestbook(g);
    } catch (err) {
      setError(err instanceof Error ? err.message : '응답을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [invitationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const attending = rsvp.filter((r) => r.attendance === '참석');
  const totalGuests = attending.reduce((sum, r) => {
    const num = Number(String(r.guests).replace(/[^0-9]/g, ''));
    return sum + (Number.isFinite(num) && num > 0 ? num : 1);
  }, 0);

  const removeRsvp = async (id: string) => {
    setRsvp((prev) => prev.filter((r) => r.id !== id));
    try {
      await deleteRsvp(id);
    } catch {
      void load();
    }
  };

  const removeGuestbook = async (id: string) => {
    setGuestbook((prev) => prev.filter((g) => g.id !== id));
    try {
      await deleteGuestbook(id);
    } catch {
      void load();
    }
  };

  return (
    <div className="pf-card" style={{ marginTop: 18 }}>
      <div className="pf-card-head">
        <h2>
          <i className="ri-mail-line" aria-hidden="true" /> 하객 응답
        </h2>
        <button type="button" className="pf-btn sm ghost" onClick={() => void load()}>
          <i className="ri-refresh-line" aria-hidden="true" />
          새로고침
        </button>
      </div>

      <div className="rp-stats">
        <div className="rp-stat">
          <span className="num">{attending.length}</span>
          <span className="lbl">참석 응답</span>
        </div>
        <div className="rp-stat">
          <span className="num">{totalGuests}</span>
          <span className="lbl">총 참석 인원</span>
        </div>
        <div className="rp-stat">
          <span className="num">{rsvp.length - attending.length}</span>
          <span className="lbl">불참</span>
        </div>
        <div className="rp-stat">
          <span className="num">{guestbook.length}</span>
          <span className="lbl">방명록 글</span>
        </div>
      </div>

      <div className="rp-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'rsvp'}
          className={`rp-tab ${tab === 'rsvp' ? 'is-active' : ''}`}
          onClick={() => setTab('rsvp')}
        >
          <i className="ri-group-line" aria-hidden="true" />
          참석 명단
          <span className="rp-count">{rsvp.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'guestbook'}
          className={`rp-tab ${tab === 'guestbook' ? 'is-active' : ''}`}
          onClick={() => setTab('guestbook')}
        >
          <i className="ri-quill-pen-line" aria-hidden="true" />
          방명록
          <span className="rp-count">{guestbook.length}</span>
        </button>
      </div>

      {error ? (
        <div className="pf-alert err" role="alert">
          <i className="ri-error-warning-line" aria-hidden="true" />
          <span>{error}</span>
          <button
            type="button"
            className="pf-btn sm ghost"
            style={{ marginLeft: 'auto' }}
            onClick={() => void load()}
          >
            다시 시도
          </button>
        </div>
      ) : null}

      {loading ? (
        <p className="pf-muted" style={{ marginTop: 14 }}>
          응답을 불러오는 중이에요…
        </p>
      ) : tab === 'rsvp' ? (
        rsvp.length === 0 ? (
          <p className="pf-muted" style={{ marginTop: 14 }}>
            아직 참석 응답이 없어요.
          </p>
        ) : (
          <div className="rp-list">
            {rsvp.map((r) => (
              <div className="rp-item" key={r.id}>
                <div className="rp-item-main">
                  <div className="rp-item-top">
                    <span className="rp-name">{r.name}</span>
                    <span className={`rp-pill ${r.attendance === '참석' ? 'ok' : 'no'}`}>
                      {r.attendance === '참석' ? '참석' : '불참'}
                    </span>
                    <span className="rp-side">{r.side} 측</span>
                  </div>
                  <div className="rp-meta">
                    {r.attendance === '참석' ? (
                      <>
                        <span>
                          <i className="ri-group-line" aria-hidden="true" />
                          {r.guests}
                        </span>
                        {r.meal ? (
                          <span>
                            <i className="ri-restaurant-line" aria-hidden="true" />
                            {r.meal}
                          </span>
                        ) : null}
                      </>
                    ) : null}
                    {r.phone ? (
                      <span>
                        <i className="ri-phone-line" aria-hidden="true" />
                        {r.phone}
                      </span>
                    ) : null}
                    <span className="rp-date">{formatDateTime(r.created_at)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="pf-btn sm ghost"
                  onClick={() => void removeRsvp(r.id)}
                  aria-label="삭제"
                >
                  <i className="ri-delete-bin-line" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )
      ) : guestbook.length === 0 ? (
        <p className="pf-muted" style={{ marginTop: 14 }}>
          아직 방명록 글이 없어요.
        </p>
      ) : (
        <div className="rp-list">
          {guestbook.map((g) => (
            <div className="rp-item" key={g.id}>
              <div className="rp-item-main">
                <div className="rp-item-top">
                  <span className="rp-name">{g.name}</span>
                  <span className="rp-side">{g.side} 측</span>
                </div>
                <p className="rp-msg">{g.message}</p>
                <span className="rp-date">{formatDateTime(g.created_at)}</span>
              </div>
              <button
                type="button"
                className="pf-btn sm ghost"
                onClick={() => void removeGuestbook(g.id)}
                aria-label="삭제"
              >
                <i className="ri-delete-bin-line" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}